import type { ReactElement } from "react";
import { Menu } from "@base-ui/react/menu";
import {
	FiCode,
	FiCopy,
	FiFileText,
	FiMoreHorizontal,
	FiTrash2,
} from "react-icons/fi";

interface ContentOptionsMenuProps {
	sourceUrl?: string;
	contentTitle?: string | null;
	markdown?: string | null;
	onRemove?: () => void;
	canToggle?: boolean;
	showRaw?: boolean;
	onToggleRaw?: () => void;
}

export function ContentOptionsMenu({
	sourceUrl,
	contentTitle,
	markdown,
	onRemove,
	canToggle,
	showRaw,
	onToggleRaw,
}: ContentOptionsMenuProps): ReactElement {
	async function handleCopyUrl() {
		if (sourceUrl) await navigator.clipboard.writeText(sourceUrl);
	}

	async function handleCopyMarkdown() {
		if (!markdown) return;
		const header = sourceUrl
			? `# ${contentTitle ?? sourceUrl}\n\nSource: ${sourceUrl}\n\n`
			: "";
		await navigator.clipboard.writeText(header + markdown);
	}

	const menuItemClasses =
		"text-fg-tertiary hover:bg-skeleton hover:text-fg-strong data-[highlighted]:bg-skeleton data-[highlighted]:text-fg-strong flex w-full cursor-default items-center gap-2.5 px-3 py-2 text-sm transition-colors outline-none";

	return (
		<Menu.Root>
			<Menu.Trigger className="border-line text-fg-faint hover:border-accent-muted hover:text-accent flex h-8 w-8 items-center justify-center rounded-full border transition-colors">
				<FiMoreHorizontal className="text-sm" />
			</Menu.Trigger>
			<Menu.Portal>
				<Menu.Positioner side="bottom" align="end" sideOffset={4}>
					<Menu.Popup className="bg-surface-raised border-line min-w-[160px] rounded-lg border py-1 shadow-lg">
						{sourceUrl && (
							<Menu.Item
								onClick={handleCopyUrl}
								className={menuItemClasses}
							>
								<FiCopy className="text-xs" />
								Copy URL
							</Menu.Item>
						)}
						{markdown && (
							<Menu.Item
								onClick={handleCopyMarkdown}
								className={menuItemClasses}
							>
								<FiFileText className="text-xs" />
								Copy as markdown
							</Menu.Item>
						)}
						{canToggle && onToggleRaw && (
							<Menu.Item
								onClick={onToggleRaw}
								className={menuItemClasses}
							>
								{showRaw ? (
									<>
										<FiFileText className="text-xs" />
										Show clean notes
									</>
								) : (
									<>
										<FiCode className="text-xs" />
										Show raw markdown
									</>
								)}
							</Menu.Item>
						)}
						{onRemove && (
							<>
								<Menu.Separator className="border-line my-1 border-t" />
								<Menu.Item
									onClick={onRemove}
									className="flex w-full cursor-default items-center gap-2.5 px-3 py-2 text-sm text-red-500 transition-colors outline-none hover:bg-red-50 hover:text-red-600 data-[highlighted]:bg-red-50 data-[highlighted]:text-red-600"
								>
									<FiTrash2 className="text-xs" />
									Remove from library
								</Menu.Item>
							</>
						)}
					</Menu.Popup>
				</Menu.Positioner>
			</Menu.Portal>
		</Menu.Root>
	);
}
